import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const texts = ['Improve relationships', 'Focus on health', 'Feel less anxious'];

const Homepage: React.FC = () => {
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [typingSpeed, setTypingSpeed] = useState(100);

  useEffect(() => {
    const currentFullText = texts[currentTextIndex];
    
    const handleTyping = () => {
      if (!isDeleting) {
        if (displayedText.length < currentFullText.length) {
          setDisplayedText(currentFullText.substring(0, displayedText.length + 1));
          setTypingSpeed(100);
        } else {
          setTimeout(() => setIsDeleting(true), 2000);
        }
      } else {
        if (displayedText.length > 0) {
          setDisplayedText(currentFullText.substring(0, displayedText.length - 1));
          setTypingSpeed(50);
        } else {
          setIsDeleting(false);
          setCurrentTextIndex((prev) => (prev + 1) % texts.length);
        }
      }
    };

    const timer = setTimeout(handleTyping, typingSpeed);
    return () => clearTimeout(timer);
  }, [displayedText, isDeleting, currentTextIndex, typingSpeed]);
  

  const guides = [
    {
      image: 'https://cdn-sc-g.sharechat.com/33d5318_1c8/tools/347b2e3d_1763449583997_sc.webp',
    },
    {
      image: 'https://cdn-sc-g.sharechat.com/33d5318_1c8/tools/2b455fb1_1763449584068_sc.webp',
    },
    {
      image: 'https://cdn-sc-g.sharechat.com/33d5318_1c8/tools/2cfd684b_1763449583850_sc.webp',
    },
    {
      image: 'https://cdn-sc-g.sharechat.com/33d5318_1c8/tools/9d97650_1763449584194_sc.webp',
    },
    {
      image: 'https://cdn-sc-g.sharechat.com/33d5318_1c8/tools/a24df61_1763449584149_sc.webp',
    },
    {
      image: 'https://cdn-sc-g.sharechat.com/33d5318_1c8/tools/2075e38c_1763449584085_sc.webp',
    },
    {
      image: 'https://cdn-sc-g.sharechat.com/33d5318_1c8/tools/33bf067d_1763449584190_sc.webp',
    },
    {
      image: 'https://cdn-sc-g.sharechat.com/33d5318_1c8/tools/59c0d00_1763449584195_sc.webp'
    },
    {
      image: 'https://cdn-sc-g.sharechat.com/33d5318_1c8/tools/8fa42f3_1763449584205_sc.webp'
    },
    {
      image: 'https://cdn-sc-g.sharechat.com/33d5318_1c8/tools/6be6b7d_1763449584196_sc.webp'
    },
    {
      image: 'https://cdn-sc-g.sharechat.com/33d5318_1c8/tools/db92eee_1763449584233_sc.webp'
    }
  ];

  const duplicatedGuides = [...guides, ...guides, ...guides];

  const handleBecomeAGuide = () => {
    window.open('https://forms.gle/fbXEuXXyoMQP8L9x5', '_blank', 'noopener,noreferrer');
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50/30 via-purple-50/30 to-cyan-50/30">
      <header className="relative z-20 flex flex-col lg:flex-row justify-between items-center px-4 lg:px-20 py-8 gap-4">
        <div className="flex items-center">
          <img 
            src="https://cdn-sc-g.sharechat.com/33d5318_1c8/tools/32031d3d_1762842169404_sc.webp" 
            alt="askk logo" 
            className="w-40" 
          />
        </div>
        
        <nav className="flex flex-col lg:flex-row items-center gap-4 lg:gap-12">
          <a 
            href="/policies" 
            className="text-black text-base font-normal hover:underline transition-all"
          >
            Policies
          </a>
          <button className="border-2 border-black rounded-2xl px-7 py-3 text-sm font-semibold hover:bg-black hover:text-white transition-all" onClick={handleBecomeAGuide}>
            Become a Guide
          </button>
        </nav>
      </header>
      <div className="relative flex flex-col items-center px-4 py-8 text-center">
        <h1 className="relative z-30 text-4xl lg:text-6xl font-normal mb-8 lg:mb-16 leading-tight">
          <span className="inline-block min-h-[1.2em]">
            {displayedText}
            <span className="animate-pulse">|</span>
          </span>
          <br />
          <span className="font-bold">with askk</span>
        </h1>
        <div className="relative z-0 w-full mb-12 lg:mb-16 overflow-hidden">
          <motion.div
            className="flex gap-6"
            animate={{
              x: [0, -((guides.length * (224 + 24)))]
            }}
            transition={{
              x: {
                repeat: Infinity,
                repeatType: "loop",
                duration: 30,
                ease: "linear"
              }
            }}
          >
            {duplicatedGuides.map((guide, index) => (
              <div
                key={`${index}`}
                className={`relative flex-shrink-0 w-56 h-80 rounded-3xl overflow-hidden shadow-2xl`}
              >
                <img 
                  src={guide.image} 
                  alt="host"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div> 
            ))}
          </motion.div>
        </div>
        <div className="relative z-30 flex flex-col items-center gap-6">
          <p className="text-lg lg:text-xl text-black">
            <strong className="font-bold">200+ trusted guides</strong> to help you feel and live better
          </p>

          <a
            href="https://play.google.com/store/apps/details?id=in.mohalla.askk"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block transition-transform hover:scale-105"
          >
            <img
              src="https://cdn-sc-g.sharechat.com/33d5318_1c8/tools/32fda499_1762842169414_sc.webp"
              alt="Get it on Google Play"
              className="h-14 lg:h-16 w-auto rounded-2xl shadow-lg"
            />
          </a>
        </div>
      </div>
    </div>
  );
};

export default Homepage;
